import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Control, divIcon, DomEvent, DomUtil, latLng, latLngBounds, LatLngBounds, LeafletMouseEvent, Layer, Map, MapOptions, Marker, MarkerCluster, MarkerClusterGroupOptions, point, tileLayer} from 'leaflet';
import {LeafletModule} from '@bluehalo/ngx-leaflet';
import {LeafletMarkerClusterDirective} from '@bluehalo/ngx-leaflet-markercluster';


@Component({
  selector: 'app-map',
  imports: [
    LeafletModule,
    LeafletMarkerClusterDirective
  ],
  templateUrl: './map.component.html',
  styleUrl: './map.component.scss'
})
export class MapComponent implements AfterViewInit, OnDestroy, OnChanges {
  private _leafletMap!: Map;
  private _layers: Layer[] = [];
  private zoomControl?: Control;

  @Input() useMarkerCluster = false;
  @Input() interactive = true;
  @Output() mapClick = new EventEmitter<{ lat: number; lon: number }>();

  @Input()
  set layers(value: Layer[]) {
    this._layers = value;
    this.handleFitBounds();
  }

  get layers(): Layer[] {
    return this._layers;
  }

  options: MapOptions = {
    layers: [
      tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {maxZoom: 18, attribution: 'openstreetmap'})
    ],
    zoomControl: false,
    center: latLng(52.52, 13.405),
    zoom: 6,
  };

  markerClusterOptions: MarkerClusterGroupOptions = {
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
    zoomToBoundsOnClick: true,
    iconCreateFunction: (cluster: MarkerCluster) => {
      const count = cluster.getChildCount();
      const size = count < 10 ? 'small' : count < 100 ? 'medium' : 'large';

      return divIcon({
        html: `
          <span class="map-cluster__pulse"></span>
          <span class="map-cluster__count">${count}</span>
        `,
        className: `map-cluster map-cluster--${size}`,
        iconSize: point(52, 52, true),
      });
    }
  };

  fitBounds: LatLngBounds = latLngBounds([[50, 10], [55, 15]]);

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      this.triggerResize();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['interactive']) {
      this.applyInteractivity();
    }
  }

  handleFitBounds() {
    if (this.layers.length === 0) return;

    const bounds = latLngBounds([]);
    let hasPoints = false;

    for (const layer of this.layers) {
      if (layer instanceof Marker) {
        bounds.extend(layer.getLatLng());
        hasPoints = true;
        continue;
      }

      const boundedLayer = layer as Layer & { getBounds?: () => LatLngBounds };
      if (typeof boundedLayer.getBounds === 'function') {
        bounds.extend(boundedLayer.getBounds());
        hasPoints = true;
      }
    }

    if (hasPoints) {
      this.fitBounds = bounds;
    }
  }

  onMapReady(map: Map) {
    this._leafletMap = map;
    this.ensureCustomPanes();
    this.applyInteractivity();
    this.addCustomZoomControl();
  }

  onMapClick(event: LeafletMouseEvent) {
    if (!this.interactive) return;

    this.mapClick.emit({
      lat: event.latlng.lat,
      lon: event.latlng.lng,
    });
  }

  triggerResize() {
    if (this._leafletMap) {
      this._leafletMap.invalidateSize();
    }
  }

  ngOnDestroy() {
    this.zoomControl?.remove();
  }

  private addCustomZoomControl() {
    if (this.zoomControl || !this._leafletMap || !this.interactive) return;

    const map = this._leafletMap;
    const zoomControl = new Control({position: 'topright'});
    let updateDisabledState: (() => void) | undefined;

    zoomControl.onAdd = () => {
      const container = DomUtil.create('div', 'leaflet-bar leaflet-control map-zoom-control');
      const zoomInButton = this.createZoomButton('+', 'Zoom in', () => map.zoomIn(), container);
      const zoomOutButton = this.createZoomButton('-', 'Zoom out', () => map.zoomOut(), container);

      updateDisabledState = () => {
        zoomInButton.disabled = map.getZoom() >= map.getMaxZoom();
        zoomOutButton.disabled = map.getZoom() <= map.getMinZoom();
      };

      map.on('zoomend zoomlevelschange', updateDisabledState);
      updateDisabledState();

      return container;
    };

    zoomControl.onRemove = () => {
      if (updateDisabledState) {
        map.off('zoomend zoomlevelschange', updateDisabledState);
      }
    };

    zoomControl.addTo(map);
    this.zoomControl = zoomControl;
  }

  private applyInteractivity() {
    if (!this._leafletMap) {
      return;
    }

    const map = this._leafletMap;

    if (this.interactive) {
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      if ((map as any).tap) {
        (map as any).tap.enable();
      }
      this.addCustomZoomControl();
      return;
    }

    map.dragging.disable();
    map.touchZoom.disable();
    map.doubleClickZoom.disable();
    map.scrollWheelZoom.disable();
    map.boxZoom.disable();
    map.keyboard.disable();
    if ((map as any).tap) {
      (map as any).tap.disable();
    }
    this.zoomControl?.remove();
    this.zoomControl = undefined;
  }

  private createZoomButton(
    label: string,
    ariaLabel: string,
    onClick: () => void,
    container: HTMLElement
  ): HTMLButtonElement {
    const button = DomUtil.create('button', 'button icon-button outlined-button map-zoom-control__button', container) as HTMLButtonElement;
    button.type = 'button';
    button.setAttribute('aria-label', ariaLabel);
    button.textContent = label;

    DomEvent.disableClickPropagation(button);
    DomEvent.on(button, 'click', (event) => {
      DomEvent.stop(event);
      onClick();
    });

    return button;
  }

  private ensureCustomPanes() {
    if (!this._leafletMap) {
      return;
    }

    const connectionPaneName = 'connectionsPane';
    const existingPane = this._leafletMap.getPane(connectionPaneName);
    if (existingPane) {
      return;
    }

    const pane = this._leafletMap.createPane(connectionPaneName);
    pane.style.zIndex = '700';
    pane.style.pointerEvents = 'none';
  }
}
